/**
 * Friend Stats Screen
 * Displays head-to-head statistics between current user and selected friend
 */

import { useEffect, useMemo, useCallback } from 'react';
import { View, Text, ActivityIndicator, Pressable, Platform } from 'react-native';
import { WEB_HOVER_TRANSITION_STYLE } from '../../../src/shared/constants/animations';
import { StatusBar } from 'expo-status-bar';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ArrowLeft } from 'lucide-react-native';
import { useSupabaseAuth } from '../../../src/shared/contexts/SupabaseAuthContext';
import { useFriendStats } from '../../../src/friends/hooks/useFriendStats';
import { GUEST_MONKEY_LOCAL_STATS_SUMMARY } from '../../../src/friends/constants/guestMonkeyLocalStats.constants';
import { useRemoveFriend } from '../../../src/friends/hooks/useFriendsMutations';
import { getRemovalConfirmationMessage, getRemovalAlertTitle, getRemovalSuccessMessage } from '../../../src/friends/helpers/friendsActions.helper';
import { FriendStatsHeader } from '../../../components/friends/friendStats/FriendStatsHeader';
import { FriendStatsLoadingSkeleton } from '../../../components/friends/friendStats/FriendStatsLoadingSkeleton';
import { PickHistoryList } from '../../../components/friends/friendStats/PickHistoryList';
import { ScreenLayout, ErrorBoundary } from '../../../components/layout';
import { getFriendStatsStyles } from '../../../appStyles/_friendStats.styles';
import { getFriendStatsScreenHeaderBarWithSafeTop } from '../../../appStyles/friendStatsScreenLayout.helper';
import { useTheme } from '../../../src/theme/useTheme';
import { useThemeContext } from '../../../src/theme/ThemeContext';
import { useDialog } from '../../../src/shared/hooks/useDialog';
import { useSnackbar } from '../../../src/shared/hooks/useSnackbar';
import { useQueryClient } from '@tanstack/react-query';
import type { Friend } from '../../../src/friends/types/friends.types';
import { MONKEY_USER_ID } from '../../../constants';

/** Persists "banner has animated" per friendId so count-up runs only once per visit. */
const hasAnimatedBannerForFriend = new Set<string>();

export default function FriendStatsScreen() {
  const { friendId } = useLocalSearchParams<{ friendId: string }>();
  const router = useRouter();
  const { isLoaded, isSignedIn, userId } = useSupabaseAuth();
  const theme = useTheme();
  const { isDark } = useThemeContext();
  const insets = useSafeAreaInsets();
  const friendStatsStyles = useMemo(() => getFriendStatsStyles(theme), [theme]);
  const screenHeaderBarStyle = useMemo(
    () => getFriendStatsScreenHeaderBarWithSafeTop(theme, insets.top),
    [theme, insets.top]
  );

  // Validate friendId (before hook call)
  const isValidFriendId = friendId && typeof friendId === 'string' && friendId.trim().length > 0;

  // Anonymous users viewing Monkey stats — no special local mode needed (they have a real session)
  const guestMonkeyLocal = false;

  const statsFriendId =
    guestMonkeyLocal ? '' : isValidFriendId && typeof friendId === 'string' ? friendId.trim() : '';

  // Call hook at top level (before any conditional returns)
  const {
    error,
    summary,
    pickHistory,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isInitialLoading,
  } = useFriendStats(statsFriendId);

  const { showDialog, showAlert } = useDialog();
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const removeFriendMutation = useRemoveFriend();
  const alreadyAnimated = friendId && hasAnimatedBannerForFriend.has(friendId);
  const shouldAnimateBanner = Boolean(summary && friendId && !alreadyAnimated);
  if (shouldAnimateBanner && friendId) hasAnimatedBannerForFriend.add(friendId);

  const handleRemoveFriend = useCallback(
    async (id: string) => {
      if (id === MONKEY_USER_ID) return;
      const isPending = false;
      const confirmed = await showDialog({
        title: getRemovalAlertTitle(isPending),
        message: getRemovalConfirmationMessage(isPending),
        type: 'danger',
        confirmText: 'Remove',
        cancelText: 'Cancel',
      });
      if (!confirmed) return;
      try {
        await removeFriendMutation.mutateAsync(id);
        if (userId) {
          // Cancel any in-flight refetches (triggered by mutation onSuccess invalidation)
          // so they don't overwrite our optimistic update
          await queryClient.cancelQueries({ queryKey: ['friends', userId] });
          queryClient.setQueryData<Friend[]>(['friends', userId], (old) =>
            old ? old.filter((f) => f.id !== id) : old
          );
        }
        showSnackbar(getRemovalSuccessMessage(false), 'success');
        router.replace('/app/friends');
      } catch {
        await showAlert({
          title: 'Error',
          message: 'Could not remove friend. Try again.',
          type: 'danger',
        });
      }
    },
    [showDialog, showAlert, showSnackbar, removeFriendMutation, router, queryClient, userId]
  );

  // Handle redirects in useEffect to avoid render-time side effects
  useEffect(() => {
    if (!isLoaded) return;
    if (!isSignedIn || !userId) {
      router.replace('/sign-in');
    } else if (!isValidFriendId) {
      router.replace('/app/friends');
    }
  }, [isLoaded, isSignedIn, userId, isValidFriendId, router]);

  const handleBackToFriends = useCallback(() => {
    router.push('/app/friends');
  }, [router]);

  const handleChallengeGuestMonkey = useCallback(() => {
    router.push({ pathname: '/app/create', params: { friendId: MONKEY_USER_ID } });
  }, [router]);

  // Show loading while auth initializes
  if (!isLoaded) {
    return (
      <ScreenLayout showBackButton={false}>
        <ActivityIndicator size="large" color={theme.white} />
      </ScreenLayout>
    );
  }

  if (guestMonkeyLocal) {
    return (
      <ErrorBoundary>
        <ScreenLayout showBackButton={false} containerStyle={friendStatsStyles.screenLayoutContainer}>
          <StatusBar style={isDark ? 'light' : 'dark'} />
          <View style={screenHeaderBarStyle}>
            <Pressable
              onPress={handleBackToFriends}
              style={({ pressed, hovered }) => [
                friendStatsStyles.screenHeaderAction,
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                pressed && friendStatsStyles.screenHeaderActionPressed,
                hovered && Platform.OS === 'web' && friendStatsStyles.screenHeaderActionHover,
              ]}
              accessibilityRole="button"
              accessibilityLabel="Go back"
            >
              <ArrowLeft size={22} color={theme.gray[800]} />
            </Pressable>
          </View>
          <FriendStatsHeader summary={GUEST_MONKEY_LOCAL_STATS_SUMMARY} />
          <PickHistoryList
            summary={GUEST_MONKEY_LOCAL_STATS_SUMMARY}
            pickHistory={[]}
            fetchNextPage={() => Promise.resolve()}
            hasNextPage={false}
            isFetchingNextPage={false}
            shouldAnimateBanner={false}
            onRemoveFriend={undefined}
            onChallengeFriend={handleChallengeGuestMonkey}
          />
        </ScreenLayout>
      </ErrorBoundary>
    );
  }

  // Don't render content if redirecting
  if (!isSignedIn || !userId || !isValidFriendId) {
    return null;
  }

  // Show loading state with skeleton
  if (isInitialLoading) {
    return (
      <ScreenLayout
        showBackButton={false}
        containerStyle={friendStatsStyles.screenLayoutContainer}
      >
        <View style={screenHeaderBarStyle}>
          <Pressable
            onPress={handleBackToFriends}
            style={({ pressed, hovered }) => [
              friendStatsStyles.screenHeaderAction,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && friendStatsStyles.screenHeaderActionPressed,
              hovered && Platform.OS === 'web' && friendStatsStyles.screenHeaderActionHover,
            ]}
            accessibilityRole="button"
            accessibilityLabel="Go back"
          >
            <ArrowLeft size={22} color={theme.gray[800]} />
          </Pressable>
        </View>
        <FriendStatsLoadingSkeleton testID="friend-stats-initial-loading" />
      </ScreenLayout>
    );
  }

  // Show error state
  if (error || !summary) {
    return (
      <ScreenLayout onBack={handleBackToFriends} containerStyle={friendStatsStyles.screenLayoutContainer}>
        <View style={friendStatsStyles.errorContainer}>
          <Text style={friendStatsStyles.errorText}>
            {error ? 'Failed to load stats' : 'Friend not found'}
          </Text>
          <Pressable
            onPress={handleBackToFriends}
            style={({ pressed, hovered }) => [
              friendStatsStyles.retryButton,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && friendStatsStyles.retryButtonPressed,
              hovered && Platform.OS === 'web' && friendStatsStyles.retryButtonHover,
            ]}
          >
            <Text style={friendStatsStyles.retryButtonText}>Go Back</Text>
          </Pressable>
        </View>
      </ScreenLayout>
    );
  }

  return (
    <ErrorBoundary>
      <ScreenLayout
        showBackButton={false}
        containerStyle={friendStatsStyles.screenLayoutContainer}
      >
        <StatusBar style={isDark ? 'light' : 'dark'} />
        <View style={screenHeaderBarStyle}>
          <Pressable
            onPress={handleBackToFriends}
            style={({ pressed, hovered }) => [
              friendStatsStyles.screenHeaderAction,
              Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
              pressed && friendStatsStyles.screenHeaderActionPressed,
              hovered && Platform.OS === 'web' && friendStatsStyles.screenHeaderActionHover,
            ]}
            accessibilityRole="button"
            accessibilityLabel="Go back"
          >
            <ArrowLeft size={22} color={theme.gray[800]} />
          </Pressable>
        </View>
        <FriendStatsHeader summary={summary} />
        <PickHistoryList
          summary={summary}
          pickHistory={pickHistory}
          fetchNextPage={fetchNextPage}
          hasNextPage={hasNextPage || false}
          isFetchingNextPage={isFetchingNextPage || false}
          shouldAnimateBanner={shouldAnimateBanner}
          onRemoveFriend={friendId && friendId !== MONKEY_USER_ID ? handleRemoveFriend : undefined}
          onChallengeFriend={
            friendId
              ? () => router.push({ pathname: '/app/create', params: { friendId } })
              : undefined
          }
        />
      </ScreenLayout>
    </ErrorBoundary>
  );
}


